import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Autocomplete from "@mui/material/Autocomplete"
import Button from "@mui/material/Button"
import ButtonGroup from "@mui/material/ButtonGroup"
import CircularProgress from "@mui/material/CircularProgress"
import Collapse from "@mui/material/Collapse"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import Drawer from "@mui/material/Drawer"
import FormControl from "@mui/material/FormControl"
import FormControlLabel from "@mui/material/FormControlLabel"
import InputAdornment from "@mui/material/InputAdornment"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { DateTimePicker } from "@mui/x-date-pickers"
import {
  addMinutes,
  formatISO,
  formatISODuration,
  intervalToDuration,
} from "date-fns"
import { useEffect, useState } from "react"
import { useDebounceValue } from "usehooks-ts"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import LengthLimitedTextField from "../../../common/components/LengthLimitedTextField"
import Row from "../../../common/components/Row"
import { useIsMobile } from "../../../common/utils"
import CandidateAvatar from "./CandidateAvatar"
import DialogHeader from "./DialogHeader"
import MeetingAttendeesStack from "./MeetingAttendeesStack"
import {
  TrialFinishedRoadblockDialogData,
  useTrialFinishedDialogUtils,
} from "./TrialFinishedRoadblockDialog"
import { meetingCreateErrorMessage } from "./calendarMeetingUtils"

export default function CreateEventDialog({ open, onClose, candidate }) {
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [eventType, setEventType] = useState(EVENT_TYPE_DEFAULT)
  const [startDateTime, setStartDateTime] = useState(null)
  const [endDateTime, setEndDateTime] = useState(null)
  const [selectedDuration, setSelectedDuration] = useState(DURATIONS[0])
  const [attendeeInputValue, setAttendeeInputValue] = useState("")
  const debouncedAttendeeInputValue = useDebounceValue(attendeeInputValue)[0]
  const [attendees, setAttendees] = useState([])
  const [isSearchingAttendees, setIsSearchingAttendees] = useState(false)
  const [attendeeOptions, setAttendeeOptions] = useState([])
  const [isCreating, setIsCreating] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const isMobile = useIsMobile()
  const axios = useAuthorizedAxiosClient()

  const Container = isMobile ? Dialog : Drawer

  function handleStartDateTimeChanged(newValue) {
    setStartDateTime(newValue)
    updateEndDateTime(selectedDuration, newValue)
  }

  function handleDurationButtonClicked(duration) {
    return () => {
      setSelectedDuration(duration)
      if (!startDateTime || duration.name === CUSTOM_DURATION_NAME) {
        return
      }
      updateEndDateTime(duration)
    }
  }

  function handleSoftClose() {
    onClose()
  }

  function updateEndDateTime(duration, start = startDateTime) {
    setEndDateTime(addMinutes(start, duration.minutes))
  }

  function handleScheduleButtonClicked() {
    setIsCreating(true)
    setErrorMessage(null)
    let formattedDuration
    if (endDateTime) {
      const duration = intervalToDuration({
        start: startDateTime,
        end: endDateTime,
      })
      formattedDuration = formatISODuration(duration).replace("0Y0M", "") // Django does not support years and months in duration field
    }
    axios
      .post("/crm/api/meeting-schedule/", {
        title,
        description,
        start_datetime: formatISO(startDateTime),
        duration: formattedDuration,
        candidates_write: attendees.map((attendee) => attendee.id),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        calendar_event_type: eventType,
      })
      .then(({ data }) => {
        onClose(data)
        clearData()
      })
      .catch((error) =>
        // For example: the user already has a meeting at that time.
        setErrorMessage(meetingCreateErrorMessage(error))
      )
      .finally(() => setIsCreating(false))
  }

  function clearData() {
    setTitle("")
    setDescription("")
    setEventType(EVENT_TYPE_DEFAULT)
    setStartDateTime(null)
    setEndDateTime(null)
    setSelectedDuration(DURATIONS[0])
    setAttendeeInputValue("")
  }

  useEffect(
    () => {
      if (candidate) {
        setAttendees(attendees.concat(candidate))
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [candidate]
  )

  useEffect(
    () => {
      if (debouncedAttendeeInputValue.length === 0) {
        setAttendeeOptions([])
        return
      }
      setIsSearchingAttendees(true)
      axios
        .get(
          `/clients/api/candidates/global-search/?q=${debouncedAttendeeInputValue}`
        )
        .then(({ data }) => setAttendeeOptions(data))
        .finally(() => setIsSearchingAttendees(false))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedAttendeeInputValue]
  )

  return (
    <Container open={open} onClose={handleSoftClose} anchor="right">
      <DialogHeader title="Add Event" onClose={handleSoftClose} />
      <DialogContent>
        <Column sx={{ gap: 3 }}>
          {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
          <LengthLimitedTextField
            value={title}
            setValue={setTitle}
            maxLength={128}
            required
            label="Meeting Title"
          />
          <LengthLimitedTextField
            value={description}
            setValue={setDescription}
            maxLength={300}
            label="Description"
            multiline
            required
          />
          <FormControl fullWidth>
            <InputLabel>Schedule Type</InputLabel>
            <Select
              label="Schedule Type"
              size="small"
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
            >
              {Object.entries(EVENT_TYPES).map(
                ([eventType, eventTypeDisplay]) => (
                  <MenuItem key={eventType} value={eventType}>
                    {eventTypeDisplay}
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>
          <DateTimePicker
            label="Date/Time *"
            value={startDateTime}
            onChange={handleStartDateTimeChanged}
          />
          <FormControlLabel
            label="How long should the meeting be?"
            labelPlacement="top"
            sx={{ alignItems: "flex-start" }}
            control={
              <ButtonGroup variant="outlined" color="inherit" size="small">
                {DURATIONS.map((duration, i) => (
                  <Button
                    key={i}
                    className={
                      selectedDuration?.name === duration.name
                        ? "active"
                        : undefined
                    }
                    onClick={handleDurationButtonClicked(duration)}
                  >
                    {duration.name}
                  </Button>
                ))}
              </ButtonGroup>
            }
          />
          <Collapse in={selectedDuration?.name === CUSTOM_DURATION_NAME}>
            <DateTimePicker
              label="End Date/Time"
              value={endDateTime}
              onChange={(newValue) => setEndDateTime(newValue)}
              minDateTime={startDateTime}
            />
          </Collapse>
          <Row sx={{ alignItems: "center", gap: 2 }}>
            <Autocomplete
              multiple
              clearOnBlur={false}
              inputValue={attendeeInputValue}
              onInputChange={(e, newValue) => setAttendeeInputValue(newValue)}
              value={attendees}
              onChange={(e, newValue) => setAttendees(newValue)}
              options={attendeeOptions}
              filterOptions={(options) => options}
              getOptionKey={(option) => option.id}
              getOptionLabel={(option) => option.candidate_name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText={
                attendeeInputValue.length === 0
                  ? "Search candidates by name..."
                  : "No candidates found"
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Attendees"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: isSearchingAttendees ? (
                      <InputAdornment position="end">
                        <CircularProgress
                          variant="indeterminate"
                          size={16}
                          color="inherit"
                        />
                      </InputAdornment>
                    ) : (
                      params.InputProps.endAdornment
                    ),
                  }}
                />
              )}
              renderOption={(props, option, state, ownerState) => (
                <Row component="li" sx={{ gap: 2 }} {...props}>
                  <CandidateAvatar
                    size="32px"
                    fontSize="14px"
                    candidate={option}
                  />
                  <Typography variant="body1" fontWeight={500}>
                    {ownerState.getOptionLabel(option)}
                  </Typography>
                </Row>
              )}
              renderTags={(tags) => (
                <Typography variant="body1" sx={{ pl: 1 }}>
                  {tags.length} Attendees
                </Typography>
              )}
              fullWidth
            />
            <MeetingAttendeesStack attendees={attendees} />
          </Row>
        </Column>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={handleSoftClose}>
          Cancel
        </Button>
        <LoadingButton
          color="primary"
          variant="contained"
          loading={isCreating}
          disabled={!title || !description || !startDateTime || !endDateTime}
          onClick={handleScheduleButtonClicked}
        >
          Schedule
        </LoadingButton>
      </DialogActions>
    </Container>
  )
}

export function useCreateEventDialogState() {
  const [createEventDialogOpen, setCreateEventDialogOpen] = useState(false)
  const { runOnlyIfSubscribed } = useTrialFinishedDialogUtils()

  function openCreateEventDialog() {
    runOnlyIfSubscribed(
      () => setCreateEventDialogOpen(true),
      new TrialFinishedRoadblockDialogData(
        "Adding Events",
        "Subscribe to create meeting events with your candidates"
      )
    )
  }

  return {
    createEventDialogOpen,
    setCreateEventDialogOpen,
    openCreateEventDialog,
  }
}

const EVENT_TYPE_DEFAULT = "default"
const EVENT_TYPES = {
  [EVENT_TYPE_DEFAULT]: "Phone Call",
  meet: "Google Meet",
}

const CUSTOM_DURATION_NAME = "Custom"

const DURATIONS = [
  {
    name: "15 min",
    minutes: 15,
  },
  {
    name: "30 min",
    minutes: 30,
  },
  {
    name: "45 min",
    minutes: 45,
  },
  {
    name: "1 h",
    minutes: 60,
  },
  {
    name: "2 h",
    minutes: 120,
  },
  {
    name: CUSTOM_DURATION_NAME,
  },
]
